import React, { useEffect, useState } from "react";
import { Trash2, Pencil, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const EMPTY = { name: "", role: "", company: "", marketplace: "Amazon", rating: 5, quote: "", featured: true };
const field = "w-full bg-white border border-[#E5E5E5] p-2.5 font-body text-xs text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md";

export default function AdminTestimonials({ notify }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, rating: Number(form.rating) };
      if (editId) await api.put(`/testimonials/${editId}`, payload); else await api.post("/testimonials", payload);
      setForm(EMPTY); setEditId(null); load(); notify(editId ? "Testimonial updated" : "Testimonial added");
    } catch (err) { setError(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/testimonials/${id}`); load(); notify("Testimonial deleted");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="admin-testimonials">
      <form onSubmit={submit} className="lg:col-span-4 bg-white border border-[#E5E5E5] p-6 rounded-md h-fit shadow-sm relative space-y-3">
        <div className="absolute top-0 left-0 w-full h-1 bg-[#6B1414]" />
        <div className="flex justify-between items-center">
          <h4 className="font-heading text-lg font-bold">{editId ? "Edit Testimonial" : "Add Testimonial"}</h4>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(EMPTY); }} className="text-gray-400 hover:text-black"><X size={16} /></button>}
        </div>
        <input data-testid="admin-tm-name-input" required placeholder="Client name" value={form.name} onChange={set("name")} className={field} />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="admin-tm-role-input" placeholder="Role (Founder)" value={form.role} onChange={set("role")} className={field} />
          <input data-testid="admin-tm-company-input" required placeholder="Company" value={form.company} onChange={set("company")} className={field} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="admin-tm-marketplace-select" value={form.marketplace} onChange={set("marketplace")} className={field}>
            {["Amazon", "Flipkart", "Meesho", "Myntra", "Ajio", "JioMart", "Snapdeal"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <select data-testid="admin-tm-rating-select" value={form.rating} onChange={set("rating")} className={field}>
            {[5, 4, 3].map((r) => <option key={r} value={r}>{`${r} stars`}</option>)}
          </select>
        </div>
        <textarea data-testid="admin-tm-quote-input" required rows="4" placeholder="Quote" value={form.quote} onChange={set("quote")} className={field} />
        {error && <p data-testid="admin-tm-error" className="font-body text-xs text-red-600">{error}</p>}
        <button data-testid="admin-tm-submit-btn" className="w-full font-body font-bold text-xs uppercase tracking-wide py-3 bg-[#2E3A9E] text-white hover:bg-[#232D7C] rounded-md">
          {editId ? "Save changes" : "Publish testimonial"}
        </button>
      </form>

      <div className="lg:col-span-8 space-y-3">
        {items.map((t) => (
          <div key={t.id} data-testid="admin-tm-row" className="bg-white border border-[#E5E5E5] p-5 rounded-md flex gap-4 justify-between">
            <div className="space-y-1 min-w-0">
              <p className="font-body text-sm text-[#0A0A0A] leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">{t.name} · {t.role} {t.company} · {t.marketplace} · {t.rating}★</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button data-testid="admin-tm-edit-btn" onClick={() => { setEditId(t.id); setForm({ name: t.name, role: t.role || "", company: t.company, marketplace: t.marketplace, rating: t.rating, quote: t.quote, featured: t.featured ?? true }); }} className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded"><Pencil size={14} /></button>
              <button data-testid="admin-tm-delete-btn" onClick={() => remove(t.id)} className="p-2 border border-red-200 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-mono text-xs text-gray-400 text-center py-10">NO TESTIMONIALS YET.</p>}
      </div>
    </div>
  );
}
